import { usePatchResolve } from 'api-hooks/approval-resolve/mutation';
import { ModelApprovalable, ResolveableType } from 'api-hooks/general/model';
import { AuthorizationRules } from 'common/constants';
import {
  SaleStatusEnum,
  PurchaseStatusEnum,
  ReleaseStatusEnum,
  TourStatusEnum,
  TransactionalStatusEnum,
  ExpenseIncomeStatusEnum,
  ProjectStatusEnum,
  RefundStatusEnum,
} from 'common/constants/enum';
import notification from 'components/common/notification';
import { Button } from 'components/elements/buttons';
import { PopoverMenuType } from 'components/popover';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import { useAuthorization } from './use-authorization';
import useDialog from './use-dialog';
import useDismiss from './use-dismiss';
import useNavigation from './use-navigation';

export function isDataRejected(
  status:
    | SaleStatusEnum
    | PurchaseStatusEnum
    | TransactionalStatusEnum
    | ReleaseStatusEnum
    | TourStatusEnum
    | ExpenseIncomeStatusEnum
    | ProjectStatusEnum
    | RefundStatusEnum
    | undefined,
) {
  switch (status) {
    case SaleStatusEnum.RejectedApproval:
    case SaleStatusEnum.RejectedRescheduleApproval:
    case ReleaseStatusEnum.RejectedReleaseApproval:
    case ReleaseStatusEnum.RejectRefund:
      return true;
    case SaleStatusEnum.Active:
    case SaleStatusEnum.Draft:
    case SaleStatusEnum.Waiting:
    case SaleStatusEnum.Archived:
    case SaleStatusEnum.Processed:
    case SaleStatusEnum.Submitted:
    case SaleStatusEnum.PendingApproval:
    case SaleStatusEnum.OpenTicket:
    case SaleStatusEnum.RescheduleDraft:
    case SaleStatusEnum.Rescheduling:
    case SaleStatusEnum.Rescheduled:
    case SaleStatusEnum.PendingRescheduleApproval:
    case SaleStatusEnum.VoidDraft:
    case SaleStatusEnum.PendingVoidApproval:
    case SaleStatusEnum.Void:
    case SaleStatusEnum.Completed:
    case SaleStatusEnum.Refund:
    case SaleStatusEnum.RevertVoidDraft:
    case SaleStatusEnum.PendingRevertVoidApproval:
    case PurchaseStatusEnum.Pending:
    case ReleaseStatusEnum.DraftRefund:
    case ReleaseStatusEnum.PendingRefund:
    case ReleaseStatusEnum.DraftCancelRefund:
    case ReleaseStatusEnum.PendingCancelRefund:
    case ReleaseStatusEnum.CancelRefund:
    case ReleaseStatusEnum.Requested:
    case ReleaseStatusEnum.PendingReleaseApproval:
    case TourStatusEnum.CancelDraft:
    case TourStatusEnum.PendingCancelApproval:
    case TourStatusEnum.Canceled:
    case TourStatusEnum.CloseDraft:
    case TourStatusEnum.PendingCloseApproval:
    case TourStatusEnum.Closed:
    case TourStatusEnum.RevertCloseDraft:
    case TourStatusEnum.PendingRevertCloseApproval:
    default:
      return false;
  }
}

export default function useResolveApproval(
  type: ResolveableType,
  data?: ModelApprovalable,
) {
  const { t } = useTranslation();
  const resolveMutate = usePatchResolve();
  const dialog = useDialog();
  const onDismiss = useDismiss(type);
  const { can } = useAuthorization();
  const { close } = useNavigation();

  const rejectApproval = React.useMemo(() => {
    return isDataRejected(data?.status);
  }, [data]);

  const permission = React.useMemo(() => {
    switch (type) {
      case 'account-payables':
        return AuthorizationRules.AccountPayablesResolve;
      case 'account-receivables':
        return AuthorizationRules.AccountReceivablesResolve;
      case 'activity-purchases':
        return AuthorizationRules.ActivityPurchasesResolve;
      case 'activity-sales':
        return AuthorizationRules.ActivitySalesResolve;
      case 'balance-adjustments':
        return AuthorizationRules.BalanceAdjustmentsResolve;
      case 'bank-transactions':
        return AuthorizationRules.BankTransactionsResolve;
      case 'cash-transactions':
        return AuthorizationRules.CashTransactionsResolve;
      case 'cruise-purchases':
        return AuthorizationRules.CruisePurchasesResolve;
      case 'cruise-sales':
        return AuthorizationRules.CruiseSalesResolve;
      case 'document-purchases':
        return AuthorizationRules.DocumentPurchasesResolve;
      case 'document-sales':
        return AuthorizationRules.DocumentSalesResolve;
      case 'flight-purchases':
        return AuthorizationRules.FlightPurchasesResolve;
      case 'flight-sales':
        return AuthorizationRules.FlightSalesResolve;
      case 'hotel-purchases':
        return AuthorizationRules.HotelPurchasesResolve;
      case 'hotel-sales':
        return AuthorizationRules.HotelSalesResolve;
      case 'order-adjustments':
        return AuthorizationRules.OrderAdjustmentsResolve;
      case 'product-purchases':
        return AuthorizationRules.ProductPurchasesResolve;
      case 'product-sales':
        return AuthorizationRules.ProductSalesResolve;
      case 'receipts':
        return AuthorizationRules.ReceiptsResolve;
      case 'refunds':
        return AuthorizationRules.RefundsResolve;
      case 'release-purchases':
        return AuthorizationRules.ReleasePurchasesResolve;
      case 'release-sales':
        return AuthorizationRules.ReleaseSalesResolve;
      case 'tour-sales':
        return AuthorizationRules.TourSalesResolve;
      case 'transfers':
        return AuthorizationRules.TransfersResolve;
    }
  }, [type]);

  const purchaseCore = React.useMemo(() => {
    switch (type) {
      case 'activity-purchases':
      case 'cruise-purchases':
      case 'document-purchases':
      case 'flight-purchases':
      case 'hotel-purchases':
      case 'product-purchases':
        return AuthorizationRules.PurchasesCore;
      default:
        return permission;
    }
  }, [permission, type]);

  const salesCore = React.useMemo(() => {
    switch (type) {
      case 'activity-sales':
      case 'cruise-sales':
      case 'document-sales':
      case 'flight-sales':
      case 'hotel-sales':
      case 'product-sales':
        return AuthorizationRules.SalesCore;
      default:
        return permission;
    }
  }, [permission, type]);

  const resolverPermission =
    (can(permission) || can(purchaseCore) || can(salesCore)) &&
    rejectApproval &&
    !data?.isResolved;

  const onResolve = React.useCallback(
    (currentId?: string) => {
      const _id = currentId ?? data?.id;
      const message = {
        type,
        id: _id || 'undefined',
        message: 'Invalid Resolve Approval Action (FE)',
      };
      if (!_id) {
        notification.error({
          message: JSON.stringify(message),
        });
        return;
      }
      dialog.showConfirmation({
        message: t('approval:confirmation_resolve_text'),
        title: t('common:confirmation'),
        onPositiveAction: async (onClose) => {
          try {
            const result = await resolveMutate.mutateAsync({
              id: _id,
              type,
            });
            notification.success({ message: result.message });
            onDismiss(_id);
            close();
          } catch (error) {
            notification.error({ message: error.message });
          } finally {
            onClose();
          }
        },
        onNegativeAction: (onClose) => {
          onClose();
        },
      });
    },
    [data?.id, type, t, dialog, resolveMutate, onDismiss, close],
  );

  const resolverAction: PopoverMenuType = {
    label: t('approval:resolve_approval'),
    onClick: onResolve,
    isAllowed: resolverPermission,
    isError: false,
    key: `resolve-approval-${data?.id}`,
    isLoading: resolveMutate.isLoading,
  };

  const resolverButton = resolverPermission && (
    <Button
      isError
      buttonType="secondary"
      isLoading={resolveMutate.isLoading}
      onClick={() => {
        onResolve();
      }}
    >
      {resolverAction.label}
    </Button>
  );

  return {
    onResolve,
    resolverPermission,
    permission,
    resolverAction,
    resolverButton,
  };
}
